import React, { Fragment, useState } from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import cellEditFactory from 'react-bootstrap-table2-editor';
import { MdAddBox } from 'react-icons/md';
import { FloatChampInput } from '../../../ressources/functionJS/FloatChampInput';
import { checkMot } from '../../../ressources/functionJS/checkMot';
import { unixTimestampToDate } from '../../../ressources/functionJS/unixTimestampToDate';
import Bouton from '../../UI/Bouton/Bouton';
import FormulaireYScale from '../ProfilCapteur/TableauComposante/ModifierComposante/FormulaireYScale/FormulaireYScale';
import styles from './AddCapteurComposante.module.scss';
import FormulaireSelectionDateUnix from './SousComposantTable/SelectionDateForUnixTimestamp/FormulaireSelectionDateUnix';

const AddCapteurComposante = (props) => {
  const [componentList, setComponentList] = useState([]);
  const [increment, setIncrement] = useState(0);
  const [incrementSubComponent, setIncrementSubComponent] = useState(1);
  const { gererComposantes } = props;

  const tableConfig = {
    columns: [
      {
        dataField: 'bouton_supprimer',
        text: checkMot('supprimer'),
        editable: false,
      },
      {
        dataField: 'component_id',
        text: checkMot('composante_id'),
        sort: true,
      },
      {
        dataField: 'position_in_sensor',
        text: checkMot('position_in_sensor'),
        sort: true,
      },
      // Simple fields with just dataField, text, and sort:true
      ...['forumula', 'prefix', 'engineering_unit', 'calibration_factors'].map(
        (field) => ({
          dataField: field,
          text: checkMot(field === 'engineering_unit' ? 'unite' : field),
          sort: true,
        })
      ),

      // Field with FloatChampInput formatter
      {
        dataField: 'precision_decimal_notation',
        text: checkMot('resolution_decimales'),
        sort: true,
        formatter: (cell) => FloatChampInput(cell),
      },

      // Date fields with FormulaireSelectionDateUnix editor and unixTimestampToDate formatter
      ...['start_formula', 'end_formula'].map((field) => ({
        dataField: field,
        text: checkMot(field),
        sort: true,
        editorRenderer: (editorProps, value) => (
          <FormulaireSelectionDateUnix
            {...editorProps}
            defaultValue={value}
            value={value}
          />
        ),
        formatter: (cell) => unixTimestampToDate(cell),
      })),

      // Y-scale field with custom editor and formatter
      {
        dataField: 'y_scale',
        text: checkMot('y_scale'),
        sort: true,
        editorRenderer: (editorProps, value) => (
          <FormulaireYScale
            {...editorProps}
            defaultValue={value}
            value={value}
          />
        ),
        formatter: (cell) => {
          return (
            '[' +
            (cell[0] === null ? 'null' : cell[0]) +
            ', ' +
            (cell[1] === null ? 'null' : cell[1]) +
            ']'
          );
        },
      },

      // Factorisation des champs simples avec sort: true
      ...[
        'time_period',
        'measures_num',
        'serial_number',
        'coordinate_x',
        'coordinate_y',
        'coordinate_z',
        'time_refresh',
      ].map((field) => ({
        dataField: field,
        text: checkMot(field),
        sort: true,
      })),

      // Factorisation des champs d'alarme
      ...[
        { level: 'high4', color: 'black' },
        { level: 'high3', color: 'red' },
        { level: 'high2', color: 'amber' },
        { level: 'high1', color: 'blue' },
        { level: 'low1', color: 'blue' },
        { level: 'low2', color: 'amber' },
        { level: 'low3', color: 'red' },
        { level: 'low4', color: 'black' },
      ].map(({ level, color }) => ({
        dataField: `alarmlevel_${level}`,
        text: checkMot(`alarmlevel_${level}_${color}`),
        sort: true,
      })),
    ],
  };

  const ajoutLigne = () => {
    const nouvelleIdDefaut = `Nouveau composant n° ${increment}`;
    const newListe = [
      ...componentList,
      {
        component_id: nouvelleIdDefaut,
        sensor_id: parseInt(props.sensor_id),
        position_in_sensor: parseInt(incrementSubComponent),
        serial_number: null,
        coordinate_x: null,
        coordinate_y: null,
        coordinate_z: null,
        forumula: null,
        prefix: null,
        engineering_unit: null,
        precision_decimal_notation: null,
        alarmlevel_high4: null,
        alarmlevel_high3: null,
        alarmlevel_high2: null,
        alarmlevel_high1: null,
        alarmlevel_low1: null,
        alarmlevel_low2: null,
        alarmlevel_low3: null,
        alarmlevel_low4: null,
        start_formula: null,
        end_formula: null,
        calibration_factors: null,
        y_scale: [null, null],
        time_period: 2678400, // un mois de 31 jours
        measures_num: 10,
        time_refresh: null,
      },
    ];
    setComponentList(newListe);
    gererComposantes(newListe);
    setIncrement(increment + 1);
    setIncrementSubComponent(incrementSubComponent + 1);
  };

  const renderDeleteButton = (composanteId) => (
    <Bouton
      cliquer={() => {
        supprimerComposante(composanteId);
      }}
    >
      {checkMot('supprimer')}
    </Bouton>
  );

  const tableData = componentList.map((component) => ({
    ...component,
    bouton_supprimer: renderDeleteButton(component.component_id),
  }));

  const supprimerComposante = (composanteId) => {
    const newComponent_list = componentList.filter((component) => {
      return component.component_id !== composanteId;
    });
    setComponentList(newComponent_list);
    gererComposantes(newComponent_list);
  };

  const cellEdit = cellEditFactory({
    mode: 'click',
    blurToSave: true,
    afterSaveCell: (oldValue, newValue, row, column) => {
      const updatedList = componentList.map((component) => {
        if (component.component_id === row.component_id) {
          // Return updated component with new value
          return {
            ...component,
            [column.dataField]: newValue,
          };
        }
        return component;
      });
      setComponentList(updatedList);
      gererComposantes(updatedList);
    },
  });

  return (
    <Fragment>
      <BootstrapTable
        striped
        hover
        keyField={'component_id'}
        data={tableData}
        columns={tableConfig.columns}
        classes={styles.TableauBootstrap}
        rowStyle={{ margin: '0px', color: 'black' }}
        cellEdit={cellEdit}
      />
      <button className={styles.btn_stylo} onClick={ajoutLigne}>
        <MdAddBox />
        {checkMot('ajouter_une_composante')}
      </button>
    </Fragment>
  );
};

export default AddCapteurComposante;
